import { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router";
import { ChevronRight } from "lucide-react";
import { MegaMenuDropdown } from "@/features/navigation/components/MegaMenu/MegaMenuDropdown";
import type { MegaMenuItemProps } from "@/features/navigation/components/MegaMenu/types/megaMenu.types";
import { cn } from "@/shared/utils/style.util";

export const MegaMenuItem = ({
  label, icon: Icon, to, submenu, featuredContent, columns, isCompact, isIconOnly,
}: MegaMenuItemProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const buttonRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  const isActive = to ? location.pathname === to : false;
  const hasSubmenu = submenu && submenu.length > 0;

  const updateDropdownPosition = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 4,
        left: rect.left,
      });
    }
  };

  const handleMouseEnter = () => {
    if (hasSubmenu) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      updateDropdownPosition();
      setIsOpen(true);
    }
  };

  const handleMouseLeave = () => {
    if (hasSubmenu) {
      timeoutRef.current = setTimeout(() => {
        setIsOpen(false);
      }, 150);
    }
  };

  const handleItemClick = () => {
    setIsOpen(false);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Escape") {
      setIsOpen(false);
    }
    else if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      setIsOpen(!isOpen);
    }
  };

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isOpen]);

  const content = (
    <div
      className={cn(
        "flex items-center rounded-lg",
        "text-sm font-semibold transition-all duration-300",
        "text-gray-700 dark:text-gray-300",
        "hover:text-primary-600 dark:hover:text-primary-400",
        "hover:bg-primary-50 dark:hover:bg-primary-900/20",
        "relative group",
        isCompact ? "gap-1.5 px-2 py-2" : "gap-2 px-4 py-2.5",
        isActive && "text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20",
      )}
    >
      {/* Active indicator */}
      {isActive && (
        <div
          className="absolute inset-x-0 bottom-0 h-0.5 bg-primary-600 dark:bg-primary-400 transition-all duration-300"
        />
      )}

      <Icon className={cn(
        "w-4 h-4 shrink-0 transition-colors duration-200",
        hasSubmenu && isOpen && "text-primary-600 dark:text-primary-400",
      )}
      />
      <span className={cn(
        "whitespace-nowrap transition-all duration-300",
        isIconOnly
          ? "max-w-0 opacity-0 overflow-hidden"
          : "max-w-[200px] opacity-100",
      )}
      >
        {label}
      </span>
      {hasSubmenu && !isIconOnly && (
        <ChevronRight className={cn(
          "w-3.5 h-3.5 ml-0.5 transition-transform duration-200",
          isOpen && "rotate-90",
        )}
        />
      )}

      {/* Tooltip (icon-only mode) */}
      {isIconOnly && (
        <span
          className={cn(
            "absolute top-full left-1/2 -translate-x-1/2 mt-1",
            "px-2 py-1 rounded text-xs font-medium whitespace-nowrap",
            "bg-gray-900 text-white dark:bg-gray-100 dark:text-gray-900",
            "opacity-0 group-hover:opacity-100 pointer-events-none",
            "transition-opacity duration-200",
            "z-50",
          )}
        >
          {label}
        </span>
      )}
    </div>
  );

  if (to && !hasSubmenu) {
    return (
      <Link
        to={to}
        className="relative"
        {...(isIconOnly && { title: label })}
      >
        {content}
      </Link>
    );
  }

  return (
    <div
      ref={buttonRef}
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <button
        type="button"
        className="w-full text-left"
        onKeyDown={handleKeyDown}
        aria-expanded={isOpen}
        aria-haspopup={hasSubmenu ? "true" : undefined}
        aria-label={`${label}${hasSubmenu ? " - Ouvrir le menu" : ""}`}
        {...(isIconOnly && { title: label })}
      >
        {content}
      </button>
      {isOpen && hasSubmenu && (
        <MegaMenuDropdown
          items={submenu}
          onItemClick={handleItemClick}
          position={dropdownPosition}
          featuredContent={featuredContent}
          columns={columns}
        />
      )}
    </div>
  );
};
